import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { analyticsAvailable, grantConsent, hasConsentDecision, revokeConsent } from "@/shared/lib/posthog";

export function CookieConsentBanner() {
  const { t } = useTranslation("common");
  const [dismissed, setDismissed] = useState(hasConsentDecision);

  // Honour Global Privacy Control — auto-decline without showing the banner.
  useEffect(() => {
    if ((navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl) {
      revokeConsent();
      setDismissed(true);
    }
  }, []);

  // No analytics here (mobile bundle, the shell, or its login leg) → nothing to
  // consent to. The apps declare no usage-data collection to the stores, and a
  // banner reading "We use analytics…" on the sign-in screen contradicts that.
  if (!analyticsAvailable() || dismissed) return null;

  function accept() {
    grantConsent();
    setDismissed(true);
  }

  function decline() {
    revokeConsent();
    setDismissed(true);
  }

  return (
    <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-50 w-[320px] border border-border bg-background shadow-xl">
      <div className="flex flex-col gap-4 p-5">
        {/* Solarpunk wordmark — light/dark SVG variants */}
        <div className="flex items-center gap-2">
          <img src="/solarpunk-logo.svg" alt="Solarpunk" className="h-6 w-6 shrink-0" />
          <p className="text-xs font-medium text-foreground">Solarpunk Technology Pvt. Ltd.</p>
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-foreground">{t("consent.title", "We value your privacy")}</p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {t("consent.body", "We count how Ubik is used without cookies so we can keep it working. Accept to also link your activity to your account and record masked session replays that help us fix what breaks. Your data is never sold.")}{" "}
            <a
              href="https://theubik.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline underline-offset-2 hover:no-underline"
            >
              {t("consent.privacy_policy", "Privacy policy")}
            </a>
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={decline}
            className="flex-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          >
            {t("consent.decline", "Decline")}
          </button>
          <button
            onClick={accept}
            className="flex-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          >
            {t("consent.accept_all", "Accept all")}
          </button>
        </div>
      </div>
    </div>
  );
}
